// Keycloak OIDC (Authorization Code + PKCE) for the waffy-staff realm.
//
// WaffySSO migration — see:
//   WaffyCTO/initiatives/07-waffy-sso/design/04-realm-topology.md  (command-center client)
//   WaffyCTO/initiatives/07-waffy-sso/design/05-staff-sso-google.md
//   WaffyCTO/initiatives/07-waffy-sso/design/11-session-and-logout.md
//
// The command-center is a PUBLIC OIDC client (SPA, no client secret), so PKCE is
// mandatory. Access tokens are sent to command-center-api as Bearer tokens; the API
// must validate them against the realm JWKS (see MIGRATION.md — coordinated API change).
//
// oidc-client-ts docs: https://github.com/authts/oidc-client-ts/blob/main/docs/index.md

import { UserManager, WebStorageStateStore, Log } from "oidc-client-ts";
import type { UserManagerSettings } from "oidc-client-ts";

// --- Env (Vite exposes only VITE_-prefixed vars to the browser) ---
// VITE_KEYCLOAK_AUTHORITY = https://id.waffyapp.com/realms/waffy-staff
// VITE_KEYCLOAK_CLIENT_ID = command-center
const AUTHORITY = import.meta.env.VITE_KEYCLOAK_AUTHORITY as string | undefined;
const CLIENT_ID = (import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string | undefined) ?? "command-center";

if (!AUTHORITY) {
  // Fail loud in dev — a missing authority silently breaks login otherwise.
  // eslint-disable-next-line no-console
  console.error(
    "[oidc] VITE_KEYCLOAK_AUTHORITY is not set. Copy .env.local.example and fill it in.",
  );
}

// Access is controlled centrally in Keycloak via the per-app access role
// `access-command-center` (assigned from Google groups — see
// WaffyCTO/initiatives/07-waffy-sso/ACCESS.md). The app only checks for that one role;
// it does NOT maintain its own list of who's allowed.
//
// The legacy staff-* roles are a TRANSITIONAL fallback so nobody is locked out before
// Keycloak finishes assigning `access-command-center`. Remove them once every current
// user holds the access role (ACCESS.md migration step 4) — then this is just
// ["access-command-center"]. Keep this list IDENTICAL to the API's WAFFY_KC_ALLOWED_ROLES.
// TRANSITIONAL list = the per-app access role + every legacy role that currently has
// access (union of the old SPA + API lists) so NOBODY is locked out mid-migration.
// Target end-state (after Keycloak assigns access-command-center to the right groups —
// ACCESS.md step 4) is simply ["access-command-center"]. Kept IDENTICAL to the API's
// WAFFY_KC_ALLOWED_ROLES.
export const ALLOWED_ROLES = [
  "access-command-center",  // ← the real gate; assigned centrally in Keycloak
  // legacy fallbacks — delete post-migration:
  "staff-ops",
  "staff-engineer",
  "staff-engineer-lead",
  "staff-data",
  "staff-admin",
  "staff-security",
] as const;

const origin = window.location.origin;

export const oidcSettings: UserManagerSettings = {
  authority: AUTHORITY ?? "",
  client_id: CLIENT_ID,
  redirect_uri: `${origin}/auth/callback`,
  silent_redirect_uri: `${origin}/auth/silent-renew`,
  post_logout_redirect_uri: `${origin}/login`,
  response_type: "code", // Authorization Code flow
  scope: "openid profile email",
  // PKCE is enabled by default in oidc-client-ts for response_type=code.
  // Front-channel single logout (11-session-and-logout.md): use the realm
  // end-session endpoint with id_token_hint so Keycloak can SLO across staff tools.
  automaticSilentRenew: true, // silent renew via hidden iframe before access-token expiry (1h)
  // Staff realm has no offline tokens; we rely on the SSO-session cookie + silent renew.
  monitorSession: false, // session-status iframe; off — Keycloak 26 blocks 3rd-party-cookie checkSessionIframe in modern browsers
  // Keep tokens in localStorage to match the app's existing same-tab UX.
  // TODO(security, H-1.3 review): consider in-memory store + refresh to reduce XSS token-theft surface.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
};

if (import.meta.env.DEV) {
  Log.setLogger(console);
  Log.setLevel(Log.WARN);
}

export const userManager = new UserManager(oidcSettings);

export function hasAllowedRole(roles: string[]): boolean {
  return roles.some((r) => (ALLOWED_ROLES as readonly string[]).includes(r));
}

// Roles permitted to DECLARE an incident — a privileged write that pages on-call,
// opens a Slack channel, and files a Jira ticket. Holding `access-command-center`
// alone grants VIEW only; declaring requires a responder/ops capability.
//
// NOTE: confirm this list against the Keycloak realm / ACCESS.md and keep it IDENTICAL
// to whatever the API enforces server-side on /command-center/incidents/declare — the
// UI gate is a convenience, not the security boundary. Target end-state is the dedicated
// `command-center-declare` capability; the staff-* entries are migration fallbacks so
// current responders aren't locked out before that role is assigned.
export const DECLARE_ROLES = [
  "command-center-declare",  // ← preferred dedicated capability; assign in Keycloak
  // responder fallbacks during migration — delete once command-center-declare is assigned:
  "staff-ops",
  "staff-engineer",
  "staff-engineer-lead",
  "staff-admin",
] as const;

export function canDeclareIncident(roles: string[]): boolean {
  return roles.some((r) => (DECLARE_ROLES as readonly string[]).includes(r));
}
